import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../config";
import moment from "moment";

import {
  Card,
  Col,
  Row,
  Typography,
  Progress,
  message,
  Button,
  Timeline,
  Tabs,
  Empty,
  notification,
  DatePicker,
  Avatar,
  Modal,
  Space,
  Result,
  Badge,
  Spin,
  Tag,
  Input,
  Alert,
  Tooltip,
  Drawer,
  Switch,
} from "antd";

import {
  EyeOutlined,
  UserOutlined,
  TrophyOutlined,
  MoneyCollectOutlined,
  TeamOutlined,
  ReloadOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  UsergroupAddOutlined,
  UsergroupDeleteOutlined,
  CrownOutlined,
  BankOutlined,
  SearchOutlined,
  RiseOutlined,
  FallOutlined,
  WarningOutlined,
  PlusOutlined,
  EnvironmentOutlined,
  NotificationOutlined,
  MessageOutlined,
  FormOutlined,
  BookOutlined,
  PictureOutlined,
  SolutionOutlined,
} from "@ant-design/icons";

import Echart from "../components/chart/EChart";
import LineChart from "../components/chart/LineChart";
import userDummy from "../assets/images/dummy-user.png";
import { hasPermission } from "../components/permissions";
import VisitorForm from "./sn_visitor/Form";
import PurnimaForm from "./sn_village/sn_purnima_form/Form";
import VisheshSahyogiForm from "./sn_vishesh_sahyogi/Form";
import RegistrationForm from "./sn_registration/Form";
import BirthdayForm from "./BirthdayForm";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Search } = Input;

// ── Helpers ────────────────────────────────────────────────────────────────────
const growthPct = (current, previous) => {
  if (!previous || previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
};

const GrowthTag = ({ current, previous }) => {
  const pct = growthPct(current, previous);
  if (pct === 0) return null;
  return pct > 0 ? (
    <Tag color="green" icon={<RiseOutlined />} style={{ borderRadius: '6px', marginLeft: '6px' }}>
      +{pct}%
    </Tag>
  ) : (
    <Tag color="red" icon={<FallOutlined />} style={{ borderRadius: '6px', marginLeft: '6px' }}>
      {pct}%
    </Tag>
  );
};

const campaignStatusColor = { draft: 'default', active: 'blue', sent: 'green' };

// ──────────────────────────────────────────────────────────────────────────────

function Home() {
  const [loading, setLoading]                     = useState(false);
  const [selectedYear, setSelectedYear]           = useState(new Date().getFullYear());
  const [selectedUserYear, setSelectedUserYear]   = useState(new Date().getFullYear());
  const [isModalVisible, setIsModalVisible]       = useState(false);
  const [currentImage, setCurrentImage]           = useState("");
  const [searchTerm, setSearchTerm]               = useState("");
  const [activeTab, setActiveTab]                 = useState("1");
  const [refreshing, setRefreshing]               = useState(false);
  const [visitorDrawerVisible, setVisitorDrawerVisible] = useState(false);
  const [purnimaDrawerVisible, setPurnimaDrawerVisible] = useState(false);
  const [visheshDrawerVisible, setVisheshDrawerVisible] = useState(false);
  const [registrationDrawerVisible, setRegistrationDrawerVisible] = useState(false);
  const [birthdayDrawerVisible, setBirthdayDrawerVisible] = useState(false);
  const [mission21kVisible, setMission21kVisible]         = useState(true);
  const [mission21kToggling, setMission21kToggling]       = useState(false);

  const [userData, setUserData] = useState({
    is_manager: false,
    total_users: 0,
    active_members: 0,
    inactive_members: 0,
    total_payments: null,
    today_payments: null,
    today_users: 0,
    this_year_payments: null,
    this_month_payments: null,
    last_month_payments: null,
    announced_amount_total: null,
    verified_payments_count: 0,
    unverified_payments_count: 0,
    members_no_payment: 0,
    this_month_members: 0,
    last_month_members: 0,
    top_contributors: [],
    district_wise: [],
    recent_campaigns: [],
    NewUserlist: [],
    NewPaymentlist: [],
    dobMonthList: [],
    memberGraph: [],
    paymentGraph: [],
    mission21k: null,
  });

  const [filters, setFilters] = useState({ entry_date_range: [] });

  // ── Fetch ─────────────────────────────────────────────────────────────────────
  const fetchDashboardData = useCallback(async (year, userYear, dateRange, showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const resp = await axios.get(`${baseUrl}/dashboard/dataList`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
        params: {
          year,
          userYear,
          startDate: dateRange?.[0] || null,
          endDate: dateRange?.[1] || null,
        },
      });
      if (resp.data.status) {
        setUserData(resp.data.result);
      } else {
        notification.warning({ message: "Warning", description: resp.data.message, placement: "topRight" });
      }
    } catch (err) {
      notification.error({ message: "Error", description: err.response?.data?.message || "Failed to load dashboard", placement: "topRight" });
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData(selectedYear, selectedUserYear, filters.entry_date_range);
  }, [selectedYear, selectedUserYear, filters.entry_date_range, fetchDashboardData]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    axios.get(`${baseUrl}/app-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const row = (r.data.result || []).find(s => s.setting_key === 'mission_21k_visible');
        if (row) setMission21kVisible(row.setting_value !== '0');
      })
      .catch(() => {});
  }, []);

  const handleMission21kToggle = async (checked) => {
    setMission21kToggling(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${baseUrl}/admin/mission-21k/visibility`, { visible: checked }, { headers: { Authorization: `Bearer ${token}` } });
      setMission21kVisible(checked);
      message.success(checked ? 'Mission 21K User Portal में दिखेगा' : 'Mission 21K User Portal से छुपाया गया');
    } catch { message.error('Toggle failed'); }
    finally { setMission21kToggling(false); }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleYearChange     = (d) => setSelectedYear(d ? d.year() : new Date().getFullYear());
  const handleUserYearChange = (d) => setSelectedUserYear(d ? d.year() : new Date().getFullYear());

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData(selectedYear, selectedUserYear, filters.entry_date_range, false);
    setRefreshing(false);
    message.success("Dashboard refreshed!");
  };

  const showModal  = (img) => { setCurrentImage(img || userDummy); setIsModalVisible(true); };
  const handleCancel = () => setIsModalVisible(false);

  // ── Stats cards ───────────────────────────────────────────────────────────────
  const statsCards = useMemo(() => {
    const cards = [];
    if (userData.is_manager) {
      cards.push({ label: "Total Donation",        value: `₹${Number(userData.total_payments || 0).toLocaleString("en-IN")}`,        icon: <MoneyCollectOutlined />, bg: "linear-gradient(135deg,#52c41a,#73d13d)", sub: "All-time collections" });
      cards.push({ label: "This Year (Apr–Mar)",   value: `₹${Number(userData.this_year_payments || 0).toLocaleString("en-IN")}`,    icon: <BankOutlined />,         bg: "linear-gradient(135deg,#13c2c2,#36cfc9)", sub: "Verified payments" });
      cards.push({ label: "Today's Donation",      value: `₹${Number(userData.today_payments || 0).toLocaleString("en-IN")}`,        icon: <TrophyOutlined />,       bg: "linear-gradient(135deg,#faad14,#ffd666)", sub: "Today's contributions" });
      cards.push({ label: "Announced Amount",      value: `₹${Number(userData.announced_amount_total || 0).toLocaleString("en-IN")}`, icon: <CrownOutlined />,        bg: "linear-gradient(135deg,#eb2f96,#f759ab)", sub: "Total pledged by members" });
    }
    cards.push({ label: "Total Members",   value: Number(userData.total_users    || 0).toLocaleString("en-IN"), icon: <TeamOutlined />,            bg: "linear-gradient(135deg,#7a0000,#2d4a73)", sub: "Registered community members" });
    cards.push({ label: "Active Members",  value: Number(userData.active_members  || 0).toLocaleString("en-IN"), icon: <UsergroupAddOutlined />,     bg: "linear-gradient(135deg,#389e0d,#52c41a)", sub: "Currently active" });
    cards.push({ label: "Inactive Members",value: Number(userData.inactive_members|| 0).toLocaleString("en-IN"), icon: <UsergroupDeleteOutlined />,  bg: "linear-gradient(135deg,#cf1322,#ff4d4f)", sub: "Currently inactive" });
    cards.push({ label: "New Today",       value: Number(userData.today_users     || 0).toLocaleString("en-IN"), icon: <UserOutlined />,             bg: "linear-gradient(135deg,#722ed1,#9254de)", sub: "Registered today" });
    return cards;
  }, [userData]);

  // ── Filtered lists ────────────────────────────────────────────────────────────
  const filteredPayments = useMemo(() =>
    (userData.NewPaymentlist || []).filter(p =>
      !searchTerm ||
      p.name_eng?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.phone_no?.includes(searchTerm) ||
      p.utr_number?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [userData.NewPaymentlist, searchTerm]);

  const filteredMembers = useMemo(() =>
    (userData.NewUserlist || []).filter(m =>
      !searchTerm ||
      m.name_eng?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.phone_no?.includes(searchTerm) ||
      m.father_name_en?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [userData.NewUserlist, searchTerm]);

  // ── Sub-components ────────────────────────────────────────────────────────────
  const StatsCard = ({ item }) => (
    <Card style={{ background: item.bg, border: "none", borderRadius: "16px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} bodyStyle={{ padding: "20px" }} hoverable>
      <Row align="middle" justify="space-between">
        <Col span={16}>
          <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px", display: "block", marginBottom: "6px" }}>{item.label}</Text>
          <Title level={3} style={{ color: "white", margin: "0 0 4px", fontWeight: 700, fontSize: "22px" }}>{item.value}</Title>
          <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: "12px" }}>{item.sub}</Text>
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: "50%", width: 56, height: 56, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "auto", fontSize: 24, color: "white" }}>
            {item.icon}
          </div>
        </Col>
      </Row>
    </Card>
  );

  const TableWrap = ({ data, type, columns, emptyText }) => (
    <div style={{ background: "white", borderRadius: "8px", overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "linear-gradient(90deg,#f8faff,#f0f5ff)" }}>
            {columns.map((c, i) => (
              <th key={i} style={{ padding: "13px 12px", textAlign: "left", fontWeight: 600, color: "#555", borderBottom: "2px solid #e8e8e8", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap" }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? data.map((item, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f5f5f5", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8faff"}
              onMouseLeave={e => e.currentTarget.style.background = ""}
            >
              {type === "payment" ? (
                <>
                  <td style={{ padding: "10px 12px", color: "#bbb", fontWeight: 600, fontSize: "13px" }}>{i + 1}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ background: "#f6ffed", borderRadius: "8px", padding: "4px 10px", display: "inline-block" }}>
                      <Text strong style={{ color: "#389e0d", fontSize: "14px" }}>₹{Number(item.amount || 0).toLocaleString("en-IN")}</Text>
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Space>
                      <Avatar size={34} icon={<UserOutlined />} src={item.profile_img || null}
                        style={{ background: `hsl(${(item.name_eng?.charCodeAt(0) || 65) * 5 % 360},60%,60%)`, flexShrink: 0 }}
                      >
                        {item.name_eng?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <div>
                        <Text strong style={{ display: "block", fontSize: "13px" }}>{item.name_eng}</Text>
                        <Text type="secondary" style={{ fontSize: "11px" }}>{item.phone_no}</Text>
                      </div>
                    </Space>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {item.utr_number
                      ? <Tag color="geekblue" style={{ borderRadius: "6px", fontFamily: "monospace", fontSize: "11px" }}>{item.utr_number}</Tag>
                      : <Text type="secondary">—</Text>}
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: "13px", color: "#555" }}>{item.transaction_date ? moment(item.transaction_date).format("DD MMM YYYY") : "—"}</td>
                  <td style={{ padding: "10px 12px", fontSize: "13px", color: "#555" }}>{item.payment_entry_date ? moment(item.payment_entry_date).format("DD MMM YYYY") : "—"}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <Tag
                      color={item.payment_virify === "verified" ? "success" : "warning"}
                      style={{ borderRadius: "20px", fontWeight: 600, fontSize: "11px" }}
                    >
                      {item.payment_virify === "verified" ? "✓ Verified" : "⏳ Pending"}
                    </Tag>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Link to={`/payments/view/${item.payment_id}`}>
                      <Button icon={<EyeOutlined />} size="small" style={{ borderRadius: "20px", fontSize: "12px", background: "#7a0000", borderColor: "#7a0000", color: "#fff" }}>View</Button>
                    </Link>
                  </td>
                </>
              ) : (
                <>
                  <td style={{ padding: "10px 12px", color: "#bbb", fontWeight: 600, fontSize: "13px" }}>{i + 1}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <Space>
                      <Avatar size={34} icon={<UserOutlined />} src={item.profile_img || null}
                        style={{ background: `hsl(${(item.name_eng?.charCodeAt(0) || 65) * 5 % 360},60%,60%)`, flexShrink: 0 }}
                      >
                        {item.name_eng?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Text strong style={{ color: "#7a0000", fontSize: "13px" }}>{item.name_eng}</Text>
                    </Space>
                  </td>
                  <td style={{ padding: "10px 12px" }}><Text copyable style={{ fontSize: "13px" }}>{item.phone_no}</Text></td>
                  <td style={{ padding: "10px 12px", fontSize: "13px", color: "#555" }}>{item.father_name_en || "—"}</td>
                  <td style={{ padding: "10px 12px", fontSize: "13px", color: "#555" }}>{item.joining_date ? moment(item.joining_date).format("DD MMM YYYY") : "—"}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <Link to={`/users/view/${item.id}`}>
                      <Button icon={<EyeOutlined />} size="small" style={{ borderRadius: "20px", fontSize: "12px", background: "#7a0000", borderColor: "#7a0000", color: "#fff" }}>View</Button>
                    </Link>
                  </td>
                </>
              )}
            </tr>
          )) : (
            <tr><td colSpan={columns.length} style={{ padding: "40px", textAlign: "center" }}><Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} /></td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // ── Permission guard ──────────────────────────────────────────────────────────
  if (!hasPermission("/dashboard", "readPermission")) {
    return (
      <div style={{ padding: "24px" }}>
        <Result status="403" title="Access Denied" subTitle="You don't have permission to view the dashboard."
          extra={<Button type="primary" onClick={() => window.history.back()}>Go Back</Button>} />
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh", padding: "24px" }}>
      <Spin spinning={loading} size="large">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <Card style={{ marginBottom: "20px", borderRadius: "16px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} bodyStyle={{ padding: "18px 24px" }}>
          <Row justify="space-between" align="middle" wrap gutter={[0, 12]}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>Dashboard</Title>
              <Text type="secondary">Community statistics · {moment().format("dddd, DD MMMM YYYY")}</Text>
            </Col>
            <Col>
              <Space wrap>
                <RangePicker
                  format="DD-MM-YYYY"
                  value={
                    filters.entry_date_range.length === 2
                      ? [moment(filters.entry_date_range[0], "YYYY-MM-DD"), moment(filters.entry_date_range[1], "YYYY-MM-DD")]
                      : []
                  }
                  onChange={(dates) => setFilters(prev => ({ ...prev, entry_date_range: dates ? dates.map(d => d.format("YYYY-MM-DD")) : [] }))}
                  placeholder={["From Date", "To Date"]}
                  style={{ borderRadius: "8px" }}
                />
                <Button icon={<ReloadOutlined spin={refreshing} />} onClick={handleRefresh} style={{ borderRadius: "8px" }}>Refresh</Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* ── Quick Access ────────────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
          {[
            { to: "/visitor",           icon: <UsergroupAddOutlined />,  label: "VisitorHub",        color: "#7a0000", bg: "#eef2f8" },
            { to: "/inquiry",           icon: <MessageOutlined />,       label: "MsgCall (Inquiry)", color: "#52c41a", bg: "#f6ffed" },
            { to: "/registration",      icon: <FormOutlined />,          label: "Registration Form", color: "#fa8c16", bg: "#fff7e6" },
            { to: "/purnima-form",      icon: <BookOutlined />,          label: "Purnima Form",      color: "#722ed1", bg: "#f9f0ff" },
            { to: "/vishesh-sahyogi",   icon: <TeamOutlined />,          label: "Vishesh Sahyogi",   color: "#d4380d", bg: "#fff2e8" },
            { to: "/comp-exam-results", icon: <TrophyOutlined />,        label: "Exam Results",      color: "#08979c", bg: "#e6fffb" },
            { to: "/festival-campaign", icon: <NotificationOutlined />,  label: "Festival Campaign", color: "#c41d7f", bg: "#fff0f6" },
            { to: "/poster",            icon: <PictureOutlined />,       label: "Message Hub",       color: "#531dab", bg: "#f9f0ff" },
            { to: "/announced",         icon: <SolutionOutlined />,      label: "Announced Form",    color: "#d46b08", bg: "#fff7e6" },
            { to: "/birthday-report",   icon: <GiftOutlined />,          label: "Birthday Form",     color: "#eb2f96", bg: "#fff0f6",  onClick: (e) => { e.preventDefault(); setBirthdayDrawerVisible(true); } },
          ].map(item => {
            const card = (
              <Card
                hoverable
                key={item.to}
                style={{ borderRadius: "12px", border: `1.5px solid ${item.bg}`, textAlign: "center", background: item.bg, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                bodyStyle={{ padding: "16px 8px" }}
                onClick={item.onClick || undefined}
              >
                <div style={{ fontSize: "26px", color: item.color, marginBottom: "6px" }}>{item.icon}</div>
                <Text strong style={{ fontSize: "13px", color: item.color }}>{item.label}</Text>
              </Card>
            );
            if (item.onClick) {
              return <div key={item.to} style={{ flex: "1 1 calc(20% - 12px)", minWidth: "130px", cursor: "pointer" }}>{card}</div>;
            }
            return (
              <Link to={item.to} key={item.to} style={{ flex: "1 1 calc(20% - 12px)", minWidth: "130px" }}>
                {card}
              </Link>
            );
          })}
        </div>

        {/* ── Alerts ─────────────────────────────────────────────────────────── */}
        <Row gutter={[16, 12]} style={{ marginBottom: "20px" }}>
          {userData.is_manager && userData.unverified_payments_count > 0 && (
            <Col xs={24} md={12}>
              <Link to="/payments?status=unverified">
                <Alert
                  message={<Text strong>{userData.unverified_payments_count} payment{userData.unverified_payments_count > 1 ? "s" : ""} pending verification</Text>}
                  description="Click to review and verify pending payments."
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ borderRadius: "10px", cursor: "pointer" }}
                />
              </Link>
            </Col>
          )}
          {userData.members_no_payment > 0 && (
            <Col xs={24} md={12}>
              <Alert
                message={<Text strong>{userData.members_no_payment} active member{userData.members_no_payment > 1 ? "s" : ""} with no payment yet</Text>}
                description="These active members have not made any payment so far."
                type="info"
                showIcon
                style={{ borderRadius: "10px" }}
              />
            </Col>
          )}
        </Row>

        {/* ── Mission 21K Tracking ────────────────────────────────────────────── */}
        {(() => {
          const m = userData.mission21k;
          if (!m) return null;
          const MISSION_TARGET = m.target || 21000;
          const enrolled       = m.total || 0;
          const pct            = Math.min(100, Math.round((enrolled / MISSION_TARGET) * 100));
          const remaining      = Math.max(0, MISSION_TARGET - enrolled);
          const monthlyRevenue = enrolled * (m.monthly_amount || 200);
          const fullRevenue    = MISSION_TARGET * (m.monthly_amount || 200);
          const now           = new Date();
          const startDate     = new Date('2026-09-25');
          const missionActive = now >= startDate;
          const graphData     = m.monthly_graph || [];

          return (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                background: '#fff',
                borderRadius: 14, padding: '18px 20px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              }}>
                {/* Top accent bar */}
                <div style={{ height: 3, borderRadius: 3, background: 'linear-gradient(90deg, #7a0000, #b45309, #fbbf24)', marginBottom: 16 }} />

                {/* Header row */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
                  <div>
                    <div style={{ fontSize:10, fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:1 }}>
                      ABRSVS — Admin Tracking
                    </div>
                    <div style={{ fontSize:22, fontWeight:900, marginTop:2, color:'#111827' }}>
                      Mission <span style={{ color:'#7a0000' }}>21K</span> Dashboard
                    </div>
                    <div style={{ fontSize:11, color:'#6b7280', marginTop:3 }}>
                      25 Sep 2026 से शुरू • जारी है &nbsp;|&nbsp; ₹200/माह/सदस्य
                    </div>
                    <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:8 }}>
                      <Switch size="small" checked={mission21kVisible} loading={mission21kToggling} onChange={handleMission21kToggle} />
                      <span style={{ fontSize:11, color: mission21kVisible ? '#7a0000' : '#9ca3af', fontWeight:600 }}>
                        {mission21kVisible ? 'User Portal में दिख रहा है' : 'User Portal में छुपा है'}
                      </span>
                    </div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:36, fontWeight:900, color:'#7a0000', lineHeight:1 }}>
                      {enrolled.toLocaleString('en-IN')}
                    </div>
                    <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>
                      enrolled / {MISSION_TARGET.toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ height:12, background:'#f3f4f6', borderRadius:6, overflow:'hidden', marginBottom:6 }}>
                  <div style={{ height:'100%', width:`${Math.max(pct,0.5)}%`, background:'linear-gradient(90deg,#7a0000,#b45309)', borderRadius:6, transition:'width 1.2s ease' }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
                  <span style={{ fontSize:11, color:'#6b7280' }}>✓ {enrolled.toLocaleString('en-IN')} enrolled</span>
                  <span style={{ fontSize:11, color:'#7a0000', fontWeight:700 }}>{remaining.toLocaleString('en-IN')} बाकी</span>
                </div>

                {/* 4 stat boxes */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
                  {[
                    { label:'इस माह जुड़े', value: m.this_month.toLocaleString('en-IN'), sub:'vs ' + m.last_month + ' पिछले माह', color:'#7a0000', bg:'#fef2f2' },
                    { label:'मासिक संग्रह',  value:'₹' + monthlyRevenue.toLocaleString('en-IN'), sub:'₹200 × ' + enrolled.toLocaleString('en-IN'), color:'#065f46', bg:'#f0fdf4' },
                    { label:'21K पर लक्ष्य', value:'₹' + (fullRevenue/100000).toFixed(1) + 'L', sub:'प्रतिमाह', color:'#92400e', bg:'#fffbeb' },
                    { label:'स्थिति', value: missionActive ? 'जारी' : 'जल्द', sub: missionActive ? '25 Sep से चालू' : 'शुरू होगा', color:'#1d4ed8', bg:'#eff6ff' },
                  ].map((s, i) => (
                    <div key={i} style={{ background:s.bg, borderRadius:10, padding:'10px 12px', textAlign:'center' }}>
                      <div style={{ fontSize:10, color:'#9ca3af', marginBottom:4 }}>{s.label}</div>
                      <div style={{ fontSize:16, fontWeight:900, color:s.color }}>{s.value}</div>
                      <div style={{ fontSize:9, color:'#9ca3af', marginTop:2 }}>{s.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Monthly bar graph */}
                {graphData.length > 0 && (
                  <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:10, padding:'12px 14px' }}>
                    <div style={{ fontSize:11, color:'#374151', marginBottom:10, fontWeight:700 }}>माहवार नामांकन</div>
                    <div style={{ display:'flex', gap:8, alignItems:'flex-end', height:60 }}>
                      {graphData.map((d, i) => {
                        const maxVal = Math.max(...graphData.map(x => Number(x.cnt)), 1);
                        const h = Math.max(4, Math.round((Number(d.cnt) / maxVal) * 52));
                        return (
                          <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                            <div style={{ fontSize:9, color:'#7a0000', fontWeight:700 }}>{Number(d.cnt)}</div>
                            <div style={{ width:'100%', height:h, background:'linear-gradient(180deg,#b45309,#7a0000)', borderRadius:'3px 3px 0 0' }} />
                            <div style={{ fontSize:8, color:'#9ca3af', textAlign:'center' }}>{d.month?.slice(5)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div style={{ marginTop:12, textAlign:'center' }}>
                  <a href="/users?campaign=mission_21k" style={{ fontSize:11, color:'#7a0000', fontWeight:600, textDecoration:'none' }}>
                    सभी Mission 21K सदस्य देखें →
                  </a>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Stats Cards ─────────────────────────────────────────────────────── */}
        <Row gutter={[18, 18]} style={{ marginBottom: "24px" }}>
          {statsCards.map((item, i) => (
            <Col key={i} xs={24} sm={12} md={8} lg={6}>
              <StatsCard item={item} />
            </Col>
          ))}
        </Row>

        {/* ── Month-over-month comparison (Manager / Accountant) ──────────────── */}
        {userData.is_manager && (
          <Row gutter={[18, 18]} style={{ marginBottom: "24px" }}>
            {/* Payments comparison */}
            <Col xs={24} md={12}>
              <Card style={{ borderRadius: "14px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} bodyStyle={{ padding: "20px 24px" }}>
                <Text strong style={{ fontSize: "14px", color: "#555" }}>
                  <MoneyCollectOutlined style={{ color: "#52c41a", marginRight: 6 }} />
                  Payments — Month Comparison
                </Text>
                <Row gutter={[16, 0]} style={{ marginTop: "16px" }}>
                  <Col span={12}>
                    <div style={{ background: "#f6ffed", borderRadius: "10px", padding: "14px", textAlign: "center" }}>
                      <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>This Month</Text>
                      <Text strong style={{ fontSize: "20px", color: "#52c41a" }}>
                        ₹{Number(userData.this_month_payments || 0).toLocaleString("en-IN")}
                      </Text>
                      <GrowthTag current={userData.this_month_payments} previous={userData.last_month_payments} />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ background: "#f5f5f5", borderRadius: "10px", padding: "14px", textAlign: "center" }}>
                      <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>Last Month</Text>
                      <Text strong style={{ fontSize: "20px", color: "#888" }}>
                        ₹{Number(userData.last_month_payments || 0).toLocaleString("en-IN")}
                      </Text>
                    </div>
                  </Col>
                </Row>
                {/* Visual bar */}
                {(() => {
                  const cur = userData.this_month_payments || 0;
                  const prev = userData.last_month_payments || 0;
                  const pct = prev > 0 ? Math.min(100, Math.round((cur / prev) * 100)) : (cur > 0 ? 100 : 0);
                  return (
                    <div style={{ marginTop: "14px" }}>
                      <Progress
                        percent={pct}
                        strokeColor={{ "0%": "#52c41a", "100%": "#73d13d" }}
                        trailColor="#e8e8e8"
                        format={p => <span style={{ fontSize: "11px", whiteSpace: "nowrap" }}>{p}% vs last month</span>}
                      />
                    </div>
                  );
                })()}
              </Card>
            </Col>

            {/* Members comparison */}
            <Col xs={24} md={12}>
              <Card style={{ borderRadius: "14px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} bodyStyle={{ padding: "20px 24px" }}>
                <Text strong style={{ fontSize: "14px", color: "#555" }}>
                  <TeamOutlined style={{ color: "#7a0000", marginRight: 6 }} />
                  New Members — Month Comparison
                </Text>
                <Row gutter={[16, 0]} style={{ marginTop: "16px" }}>
                  <Col span={12}>
                    <div style={{ background: "#eef2f8", borderRadius: "10px", padding: "14px", textAlign: "center" }}>
                      <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>This Month</Text>
                      <Text strong style={{ fontSize: "20px", color: "#7a0000" }}>
                        {userData.this_month_members || 0}
                      </Text>
                      <GrowthTag current={userData.this_month_members} previous={userData.last_month_members} />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ background: "#f5f5f5", borderRadius: "10px", padding: "14px", textAlign: "center" }}>
                      <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>Last Month</Text>
                      <Text strong style={{ fontSize: "20px", color: "#888" }}>
                        {userData.last_month_members || 0}
                      </Text>
                    </div>
                  </Col>
                </Row>
                {(() => {
                  const cur = userData.this_month_members || 0;
                  const prev = userData.last_month_members || 0;
                  const pct = prev > 0 ? Math.min(100, Math.round((cur / prev) * 100)) : (cur > 0 ? 100 : 0);
                  return (
                    <div style={{ marginTop: "14px" }}>
                      <Progress
                        percent={pct}
                        strokeColor={{ "0%": "#7a0000", "100%": "#2d4a73" }}
                        trailColor="#e8e8e8"
                        format={p => <span style={{ fontSize: "11px", whiteSpace: "nowrap" }}>{p}% vs last month</span>}
                      />
                    </div>
                  );
                })()}
              </Card>
            </Col>
          </Row>
        )}

        {/* ── Payment Verification bar ────────────────────────────────────────── */}
        {userData.is_manager && (
          <Card style={{ marginBottom: "24px", borderRadius: "14px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} bodyStyle={{ padding: "18px 24px" }}>
            <Row gutter={[24, 12]} align="middle">
              <Col xs={24} sm={8}>
                <Text strong style={{ fontSize: "14px" }}>
                  <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 6 }} />
                  Payment Verification Status
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: "13px" }}>
                  {userData.verified_payments_count} verified &nbsp;·&nbsp; {userData.unverified_payments_count} pending
                </Text>
              </Col>
              <Col xs={24} sm={16}>
                {(() => {
                  const total = (userData.verified_payments_count || 0) + (userData.unverified_payments_count || 0);
                  const pct = total > 0 ? Math.round((userData.verified_payments_count / total) * 100) : 0;
                  return (
                    <Progress
                      percent={pct}
                      strokeColor={{ "0%": "#52c41a", "100%": "#73d13d" }}
                      trailColor="#ff4d4f"
                      format={p => <span style={{ fontSize: "13px", fontWeight: 600 }}>{p}% Verified</span>}
                    />
                  );
                })()}
              </Col>
            </Row>
          </Card>
        )}

        {/* ── Main content + birthdays ────────────────────────────────────────── */}
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <Card style={{ borderRadius: "16px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)", minHeight: 600 }} bodyStyle={{ padding: "24px" }}
              title={<Title level={4} style={{ margin: 0 }}>Recent Activities</Title>}
            >
              {/* Search inside body so it doesn't get clipped by card title overflow */}
              <div style={{ marginBottom: "12px" }}>
                <Search
                  placeholder="Search members, payments…"
                  allowClear
                  style={{ width: "100%", maxWidth: 320 }}
                  onChange={e => setSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                />
              </div>
              <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" style={{ marginTop: 4 }}>
                <TabPane tab={<Space>Recent Payments <Badge count={filteredPayments.length} style={{ backgroundColor: "#52c41a" }} /></Space>} key="1">
                  <TableWrap data={filteredPayments} type="payment"
                    columns={["#", "Amount", "Member", "UTR/ID", "Txn Date", "Entry Date", "Status", "Action"]}
                    emptyText="No recent payments" />
                </TabPane>

                <TabPane tab={<Space><TeamOutlined />New Members <Badge count={filteredMembers.length} style={{ backgroundColor: "#7a0000" }} /></Space>} key="2">
                  <TableWrap data={filteredMembers} type="member"
                    columns={["#", "Member Name", "Phone", "Father Name", "Joining Date", "Action"]}
                    emptyText="No new members" />
                </TabPane>

                {userData.is_manager && (
                  <TabPane tab={<Space><CrownOutlined style={{ color: "#faad14" }} />Top Contributors</Space>} key="3">
                    {userData.top_contributors?.length > 0 ? (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ background: "linear-gradient(90deg,#fffbe6,#fff7e6)" }}>
                              {["Rank", "Member", "Phone", "Payments", "Total Paid", "Action"].map((c, i) => (
                                <th key={i} style={{ padding: "13px 12px", textAlign: "left", fontWeight: 600, color: "#555", borderBottom: "2px solid #ffe58f", fontSize: "12px", textTransform: "uppercase" }}>{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {userData.top_contributors.map((m, i) => (
                              <tr key={i} style={{ borderBottom: "1px solid #fff7e6" }}>
                                <td style={{ padding: "12px" }}>
                                  {i === 0 ? <CrownOutlined style={{ color: "#faad14", fontSize: 20 }} /> :
                                   i === 1 ? <CrownOutlined style={{ color: "#8c8c8c", fontSize: 18 }} /> :
                                   i === 2 ? <CrownOutlined style={{ color: "#d46b08", fontSize: 16 }} /> :
                                   <Text type="secondary" style={{ fontWeight: 600 }}>#{i + 1}</Text>}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  <Space>
                                    <Avatar size={36} src={m.profile_img || userDummy} icon={<UserOutlined />} style={{ cursor: "pointer" }} onClick={() => showModal(m.profile_img)} />
                                    <Text strong>{m.name || m.name_eng}</Text>
                                  </Space>
                                </td>
                                <td style={{ padding: "12px" }}><Text copyable>{m.phone_no}</Text></td>
                                <td style={{ padding: "12px" }}><Tag color="blue" style={{ borderRadius: "6px" }}>{m.payment_count} payments</Tag></td>
                                <td style={{ padding: "12px" }}><Text strong style={{ color: "#52c41a", fontSize: 15 }}>₹{Number(m.total_paid).toLocaleString("en-IN")}</Text></td>
                                <td style={{ padding: "12px" }}><Link to={`/users/view/${m.id}`}><Button type="primary" icon={<EyeOutlined />} size="small" style={{ borderRadius: "6px" }}>View</Button></Link></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : <Empty description="No payment data available" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "40px 0" }} />}
                  </TabPane>
                )}
              </Tabs>
            </Card>
          </Col>

          {/* Birthdays */}
          <Col xs={24} lg={8}>
            <Card
              style={{ borderRadius: "16px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)", height: 600 }}
              bodyStyle={{ padding: "24px", height: "100%", display: "flex", flexDirection: "column" }}
              title={<Space><GiftOutlined style={{ color: "#faad14" }} /><span>Birthday Reminders</span><Badge count={userData.dobMonthList?.length || 0} style={{ backgroundColor: "#faad14" }} /></Space>}
            >
              <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>Upcoming birthdays (sorted by date)</Text>
              <div style={{ flex: 1, overflowY: "auto" }}>
                {userData.dobMonthList?.length > 0 ? (
                  <Timeline>
                    {userData.dobMonthList.map((m, i) => (
                      <Timeline.Item key={i} color="#faad14" dot={<GiftOutlined style={{ fontSize: 14 }} />}>
                        <Card size="small" style={{ marginBottom: 10, borderRadius: 8, border: "1px solid #f0f0f0" }} bodyStyle={{ padding: 10 }}>
                          <Row align="middle" gutter={[10, 0]}>
                            <Col><Avatar size={38} src={m.profile_img || userDummy} icon={<UserOutlined />} onClick={() => showModal(m.profile_img || userDummy)} style={{ cursor: "pointer" }} /></Col>
                            <Col flex={1}>
                              <Text strong style={{ display: "block", fontSize: 13 }}>{m.name}</Text>
                              {m.announced_amount && <Text type="success" style={{ fontSize: 12 }}>₹{Number(m.announced_amount).toLocaleString("en-IN")}</Text>}
                            </Col>
                            <Col><Tag color="orange" style={{ borderRadius: 12, fontSize: 12 }}>{m.dob ? moment(m.dob).format("DD MMM") : "-"}</Tag></Col>
                          </Row>
                        </Card>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                ) : <Empty description="No upcoming birthdays" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 60 }} />}
              </div>
              <div style={{ marginTop: 16 }}>
                <Link to="/reports/birthday-list"><Button block style={{ borderRadius: 8, background: "#7a0000", borderColor: "#7a0000", color: "#fff" }}>View All Birthdays</Button></Link>
              </div>
            </Card>
          </Col>
        </Row>

        {/* ── District Distribution + Recent Campaigns ────────────────────────── */}
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          {/* District breakdown */}
          <Col xs={24} md={12}>
            <Card style={{ borderRadius: "16px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} bodyStyle={{ padding: "24px" }}
              title={<Space><EnvironmentOutlined style={{ color: "#7a0000" }} /><span>Member Distribution by District</span></Space>}
            >
              {userData.district_wise?.length > 0 ? (() => {
                const maxCount = Math.max(...userData.district_wise.map(d => d.count), 1);
                return (
                  <div>
                    {userData.district_wise.map((d, i) => (
                      <div key={i} style={{ marginBottom: 12 }}>
                        <Row justify="space-between" style={{ marginBottom: 4 }}>
                          <Text strong style={{ fontSize: 13 }}>{d.district}</Text>
                          <Text type="secondary" style={{ fontSize: 13 }}>{d.count} members</Text>
                        </Row>
                        <Progress
                          percent={Math.round((d.count / maxCount) * 100)}
                          showInfo={false}
                          strokeColor={`hsl(${200 + i * 18}, 70%, 50%)`}
                          trailColor="#f0f0f0"
                          size="small"
                        />
                      </div>
                    ))}
                  </div>
                );
              })() : <Empty description="No district data" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </Card>
          </Col>

          {/* Recent Campaigns */}
          <Col xs={24} md={12}>
            <Card style={{ borderRadius: "16px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} bodyStyle={{ padding: "24px" }}
              title={<Space><NotificationOutlined style={{ color: "#722ed1" }} /><span>Recent Campaigns</span></Space>}
              extra={<Link to="/festival-campaign"><Button size="small" style={{ borderRadius: 6 }}>View All</Button></Link>}
            >
              {userData.recent_campaigns?.length > 0 ? (
                <div>
                  {userData.recent_campaigns.map((c, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: i < userData.recent_campaigns.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ display: "block", fontSize: 13 }}>{c.name}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>{c.festival_type} · {moment(c.created_at).format("DD MMM YYYY")}</Text>
                      </div>
                      <Space>
                        {c.total_sent > 0 && (
                          <Tooltip title={`${c.sent_success} delivered / ${c.total_sent} sent`}>
                            <Tag color="blue" style={{ borderRadius: 6 }}>{c.sent_success}/{c.total_sent}</Tag>
                          </Tooltip>
                        )}
                        <Tag color={campaignStatusColor[c.status] || "default"} style={{ borderRadius: 6 }}>{c.status}</Tag>
                        <Link to={`/festival-campaign/send/${c.id}`}>
                          <Button size="small" icon={<NotificationOutlined />} style={{ borderRadius: 6 }}>Send</Button>
                        </Link>
                      </Space>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty description="No campaigns yet" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                  <Link to="/festival-campaign/add"><Button type="primary" icon={<PlusOutlined />} style={{ borderRadius: 8 }}>Create Campaign</Button></Link>
                </Empty>
              )}
            </Card>
          </Col>
        </Row>

        {/* ── Charts ──────────────────────────────────────────────────────────── */}
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={10}>
            <Card style={{ borderRadius: "16px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)", height: 400 }} bodyStyle={{ padding: 24 }}
              title={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Space><TeamOutlined style={{ color: "#7a0000" }} /><span>Member Growth</span></Space>
                  <DatePicker picker="year" defaultValue={moment(String(selectedUserYear), "YYYY")} onChange={handleUserYearChange} style={{ borderRadius: 8 }} />
                </div>
              }
            >
              <Echart data={userData.memberGraph} totalMembers={userData.active_members} />
            </Card>
          </Col>
          <Col xs={24} lg={14}>
            <Card style={{ borderRadius: "16px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)", height: 400 }} bodyStyle={{ padding: 24 }}
              title={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Space><MoneyCollectOutlined style={{ color: "#52c41a" }} /><span>Payment Trends</span></Space>
                  <DatePicker picker="year" defaultValue={moment(String(selectedYear), "YYYY")} onChange={handleYearChange} style={{ borderRadius: 8 }} />
                </div>
              }
            >
              <LineChart data={userData.paymentGraph} isManager={userData.is_manager} />
            </Card>
          </Col>
        </Row>

      </Spin>

      {/* Profile Image Modal */}
      <Modal open={isModalVisible} footer={null} onCancel={handleCancel} centered width="auto" style={{ maxWidth: "90vw" }}>
        <img src={currentImage} alt="Profile" style={{ width: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: 12 }} />
      </Modal>

      {/* ── Floating Right: Visitor + Purnima + Vishesh Sahyogi + Registration ── */}
      <div style={{ position: "fixed", right: "16px", top: "50%", transform: "translateY(-50%)", display: "flex", flexDirection: "column", gap: "12px", zIndex: 999 }}>
        {[
          { action: () => setVisitorDrawerVisible(true),      icon: <UsergroupAddOutlined />, label: "Visitor Form",     color: "#7a0000", bg: "#eef2f8" },
          { action: () => setPurnimaDrawerVisible(true),      icon: <BookOutlined />,         label: "Purnima Form",     color: "#722ed1", bg: "#f9f0ff" },
          { action: () => setVisheshDrawerVisible(true),      icon: <TeamOutlined />,         label: "Vishesh Sahyogi",  color: "#d4380d", bg: "#fff2e8" },
          { action: () => setRegistrationDrawerVisible(true), icon: <FormOutlined />,         label: "Registration",     color: "#fa8c16", bg: "#fff7e6" },
          { action: () => setBirthdayDrawerVisible(true),     icon: <GiftOutlined />,         label: "Birthday Form",    color: "#eb2f96", bg: "#fff0f6" },
        ].map((item, i) => (
          <Tooltip key={i} title={item.label} placement="left">
            <div onClick={item.action} style={{ width: 48, height: 48, borderRadius: "50%", background: item.bg, border: `2px solid ${item.color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: item.color, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", cursor: "pointer", transition: "transform 0.2s, box-shadow 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.15)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.18)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)"; }}
            >{item.icon}</div>
          </Tooltip>
        ))}
      </div>

      {/* ── Add Purnima Drawer ────────────────────────────────────────────── */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BookOutlined style={{ color: "white", fontSize: 16 }} />
            </div>
            <div>
              <div style={{ color: "white", fontWeight: 600, fontSize: 15 }}>Add New Purnima Member</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>Create a new Purnima member entry</div>
            </div>
          </div>
        }
        width={window.innerWidth > 768 ? 700 : "95%"}
        open={purnimaDrawerVisible}
        onClose={() => setPurnimaDrawerVisible(false)}
        headerStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        bodyStyle={{ padding: "24px", background: "#f8f9fa" }}
        destroyOnClose
      >
        <PurnimaForm onRefreshData={() => setPurnimaDrawerVisible(false)} />
      </Drawer>

      {/* ── Add Vishesh Sahyogi Drawer ─────────────────────────────────────── */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TeamOutlined style={{ color: "white", fontSize: 16 }} />
            </div>
            <div>
              <div style={{ color: "white", fontWeight: 600, fontSize: 15 }}>Add New Vishesh Sahyogi</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>Create a new Vishesh Sahyogi member</div>
            </div>
          </div>
        }
        width={window.innerWidth > 768 ? 700 : "95%"}
        open={visheshDrawerVisible}
        onClose={() => setVisheshDrawerVisible(false)}
        headerStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        bodyStyle={{ padding: "24px", background: "#f8f9fa" }}
        destroyOnClose
      >
        <VisheshSahyogiForm onRefreshData={() => setVisheshDrawerVisible(false)} />
      </Drawer>

      {/* ── Add Visitor Drawer ─────────────────────────────────────────────── */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <PlusOutlined style={{ color: "white", fontSize: 16 }} />
            </div>
            <div>
              <div style={{ color: "white", fontWeight: 600, fontSize: 15 }}>Add New Visitor</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>Register a new visitor</div>
            </div>
          </div>
        }
        width={window.innerWidth > 768 ? 560 : "95%"}
        open={visitorDrawerVisible}
        onClose={() => setVisitorDrawerVisible(false)}
        headerStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        bodyStyle={{ padding: "24px", background: "#f8f9fa" }}
        destroyOnClose
      >
        <VisitorForm onRefreshData={() => setVisitorDrawerVisible(false)} />
      </Drawer>

      {/* ── Add Registration Drawer ────────────────────────────────────────── */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FormOutlined style={{ color: "white", fontSize: 16 }} />
            </div>
            <div>
              <div style={{ color: "white", fontWeight: 600, fontSize: 15 }}>Add New Registration</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>Create a new registration entry</div>
            </div>
          </div>
        }
        width={window.innerWidth > 768 ? 700 : "95%"}
        open={registrationDrawerVisible}
        onClose={() => setRegistrationDrawerVisible(false)}
        headerStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        bodyStyle={{ padding: "24px", background: "#f8f9fa" }}
        destroyOnClose
      >
        <RegistrationForm onRefreshData={() => setRegistrationDrawerVisible(false)} />
      </Drawer>

      {/* ── Birthday Form Drawer ───────────────────────────────────────────── */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GiftOutlined style={{ color: "white", fontSize: 16 }} />
            </div>
            <div>
              <div style={{ color: "white", fontWeight: 600, fontSize: 15 }}>जन्मदिन पोस्टर पंजीकरण</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>Add new external birthday contact</div>
            </div>
          </div>
        }
        width={window.innerWidth > 768 ? 600 : "95%"}
        open={birthdayDrawerVisible}
        onClose={() => setBirthdayDrawerVisible(false)}
        headerStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        bodyStyle={{ padding: "24px", background: "#f8f9fa" }}
        destroyOnClose
      >
        <BirthdayForm drawerMode onSuccess={() => setBirthdayDrawerVisible(false)} />
      </Drawer>

      <style jsx>{`
        .ant-card { transition: all 0.3s ease !important; }
        .ant-card:hover { transform: translateY(-2px) !important; box-shadow: 0 12px 32px rgba(0,0,0,0.15) !important; }
        table tbody tr { transition: background 0.2s ease !important; }
        table tbody tr:hover { background: #f8faff !important; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
      `}</style>
    </div>
  );
}

export default Home;
